import type {
  Frags_Form_Email_Settings_Fragment$data,
  Frags_Form_Metadata_Settings_Fragment$data,
} from '@/graphql/queries/Frags.query';
import { FormMetadataStyleEnum, FormMetadataTemplateEnum } from '@/graphql/types';
import { type FormEmailState, initFormEmailState } from '@/routes/dash/crm/forms/-form-page/EmailOptions';
import type { Writable } from 'ts-essentials';

export enum FormCookieExpirationEnum {
  Immediate = '0,0',
  Five_Minutes = '0,5',
  Day = '1,0',
  Week = '7,0',
  Month = '30,0',
  Half_Year = '182,0',
  Year = '365,0',
}

export const initFormMetadata: Omit<Frags_Form_Metadata_Settings_Fragment$data, ' $fragmentType'> = Object.freeze({
  formStyle: FormMetadataStyleEnum.NoImage,
  formTemplate: FormMetadataTemplateEnum.Basic,
  cookieExpiration: FormCookieExpirationEnum.Month,
  submitSuccessText: null,
});

export type FormMetadata = Writable<Partial<typeof initFormMetadata>>;

export const initFormState = Object.freeze({
  title: '' as string,
  description: '' as string,
  schema: '{}' as string,
  uiSchema: '{}' as string,
  extraData: '' as string,
  metadata: initFormMetadata as Omit<Frags_Form_Metadata_Settings_Fragment$data, ' $fragmentType'> | null,
  email: initFormEmailState as FormEmailState | Frags_Form_Email_Settings_Fragment$data | null,
  password: '' as string | true | null,
  imageUrl: null as string | null,
  marketingConsent: false as boolean,
});

export type FormState = Writable<typeof initFormState>;

// generate types for schema and uiSchema
type AllowedPropertyKeys =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone_number'
  | 'company_name'
  | 'job_role'
  | 'marketing_consent'
  | 'message'
  | 'password';
type PropertyString = {
  title: string;
  type: 'string';
  format?: 'email' | 'hostname' | 'uri' | 'regex';
  pattern?: string;
  default?: string;
  description?: string;
  maxLength?: number;
  minLength?: number;
};
type PropertyBoolean = {
  title: string;
  type: 'boolean';
  default?: 'true' | 'false';
};
type PropertyTypes = PropertyString | PropertyBoolean;
export type Schema = {
  type: 'object';
  title?: string;
  description?: string;
  properties: {
    [key in AllowedPropertyKeys]?: PropertyTypes;
  };
  dependencies: Record<string, never>;
  required?: Array<keyof Schema['properties']>;
};
export type UiSchema<T extends Schema> = {
  [key in keyof T['properties']]?: {
    'ui:title'?: string;
    'ui:placeholder'?: string;
    'ui:autocomplete'?: 'email' | 'username' | 'password' | 'country' | 'street-address';
    'ui:autofocus'?: boolean;
    'ui:classNames'?: string;
    'ui:widget'?: 'password';
    norender?: boolean;
    props?: {
      disabled?: boolean;
      className?: string;
      hidden?: boolean;
    };
  };
} & {
  'ui:order'?: Array<keyof T['properties']>;
};
